import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, InputNumber, Select, Switch, Modal } from 'antd';
import { TASK_STATUSES } from '../../_shared/constants';

const STATUS_I18N_MAP = {
  OPEN: 'taskStatusOpen',
  IN_PROGRESS: 'taskStatusInProgress',
  DONE: 'taskStatusDone',
};

export default function TaskForm({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  members = [],
  isLoading = false,
}) {
  const { t } = useTranslation(['forms']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name: initialValues.name,
          description: initialValues.description,
          status: initialValues.status || 'OPEN',
          estimatedHours: initialValues.estimatedHours,
          assignees: initialValues.assignees?.map((a) => a._id || a) || [],
          billable: initialValues.billable,
          hourlyRate: initialValues.hourlyRate,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch {
      // validation failed
    }
  };

  const memberOptions = members.map((m) => {
    const emp = m.employee || m;
    return {
      label: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp._id,
      value: emp._id || emp,
    };
  });

  return (
    <Modal
      title={
        initialValues
          ? t('editTask', { ns: 'forms' })
          : t('addTask', { ns: 'forms' })
      }
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="btn-default" onClick={onClose}>
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Button
            className="btn-primary"
            loading={isLoading}
            onClick={handleOk}
          >
            {t('save', { ns: 'forms' })}
          </Button>
        </div>
      }
      destroyOnClose
      width={480}
    >
      <Form form={form} layout="vertical" initialValues={{ status: 'OPEN' }}>
        <Form.Item
          name="name"
          label={t('taskName', { ns: 'forms' })}
          rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="description" label={t('description', { ns: 'forms' })}>
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item name="status" label={t('status', { ns: 'forms' })}>
          <Select
            options={Object.keys(TASK_STATUSES).map((key) => ({
              label: t(STATUS_I18N_MAP[key], { ns: 'forms' }),
              value: key,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="estimatedHours"
          label={t('estimatedHours', { ns: 'forms' })}
        >
          <InputNumber min={0} step={0.5} className="w-full" />
        </Form.Item>

        <Form.Item name="assignees" label={t('assignees', { ns: 'forms' })}>
          <Select
            mode="multiple"
            options={memberOptions}
            placeholder={t('selectAssignees', { ns: 'forms' })}
            allowClear
          />
        </Form.Item>

        <div className="flex gap-4">
          <Form.Item
            name="billable"
            label={t('billable', { ns: 'forms' })}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="hourlyRate" label={t('hourlyRate', { ns: 'forms' })}>
            <InputNumber min={0} className="w-full" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
